import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {
  BokkzIbDataService,
  ExtraUitgavenKledingEnBeddengoed,
  IncomeTaxEntity,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {CommonModule, DatePipe} from '@angular/common';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  Subscription,
  switchMap,
  take,
  tap
} from 'rxjs';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiTooltipModule} from '@snelstart/ui-tooltip';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

type PGAextraUitgavenKledingEnBeddengoedFormGroup = FormGroup<{
  duurZiekteMinimaalJaar: FormControl<boolean | null>;
  extraUitgavenKledingBeddengoedHogerDanMax: FormControl<boolean | null>;
  periode: FormGroup<{
    begindatum: FormControl<Date | null>;
    einddatum: FormControl<Date | null>;
  }>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-extra-uitgaven-kleding-en-beddengoed',
  templateUrl: './extra-uitgaven-kleding-en-beddengoed.component.html',
  styleUrls: ['./extra-uitgaven-kleding-en-beddengoed.component.scss'],
  imports: [SharedUiFormControlsModule, SharedUiButtonModule, SharedUiDetailsModule, CommonModule, SharedUiTooltipModule],
  providers: [DatePipe],
})
export class ExtraUitgavenKledingEnBeddengoedComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  private incomeTaxEntity = {} as IncomeTaxEntity;

  public formGroup = new FormGroup({
    extraUitgavenKledingEnBeddengoed: new FormArray<PGAextraUitgavenKledingEnBeddengoedFormGroup>([]),
  });

  public itemsDuurZiekte: SelectFormControlItem<boolean>[] = [
    {value: true, label: 'Langer dan een jaar'},
    {value: false, label: 'Korter dan een jaar'},
  ];

  public itemsJaNee: SelectFormControlItem<boolean>[] = [];

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
    filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
    takeUntilDestroyed(this.destroyRef),
    shareReplay({bufferSize: 1, refCount: true}),
  );

  public ngOnInit(): void {
    this.incomeTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((data) => {
      this.setItemsJaNee(data);
      this.updateForm(data.aangifte?.persoonsgebondenAftrek?.pgaSpecifiekeZorgkosten?.extraUitgavenKledingEnBeddengoed || []);
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    const extractRelevant = () =>
      this.extraUitgavenKledingEnBeddengoed.getRawValue().map(
        (w): ExtraUitgavenKledingEnBeddengoed => ({
          duurZiekteMinimaalJaar: w.duurZiekteMinimaalJaar,
          extraUitgavenKledingBeddengoedHogerDanMax: w.extraUitgavenKledingBeddengoedHogerDanMax,
          periode: {
            begindatum: w.periode.begindatum,
            einddatum: w.periode.einddatum,
          },
        }),
      );

    this.changeSubscriptions.add(
      this.extraUitgavenKledingEnBeddengoed.valueChanges
        .pipe(
          debounceTime(500),
          tap(() => this.saveForm()),
          map(() => extractRelevant()),
          distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
          switchMap(() => this.dataService.calculateLatestIncomeTaxReturn()),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(),
    );
  }

  setItemsJaNee(entity : IncomeTaxEntity) {
    let grensbedrag = 700;
    if (entity.year === 2025) {
      grensbedrag = 680;
    }
    if (entity.year === 2026) {
      grensbedrag = 660;
    }

    this.itemsJaNee.push({value: true, label: 'Uitgaven hoger dan € ' + grensbedrag});
    this.itemsJaNee.push({value: false, label: 'Uitgaven lager dan € ' + grensbedrag});
  }

  get extraUitgavenKledingEnBeddengoed(): FormArray {
    return this.formGroup.controls.extraUitgavenKledingEnBeddengoed
  }

  getPeriodeFormGroup(index: number): FormGroup {
    const periode = this.extraUitgavenKledingEnBeddengoed.at(index) as FormGroup;
    return periode.get('periode') as FormGroup;
  }

  addRij(): void {
    this.extraUitgavenKledingEnBeddengoed.push(this.createFormGroup(), {emitEvent: false});
  }

  verwijderRij(index: number): void {
    this.extraUitgavenKledingEnBeddengoed.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false});
    this.formGroup.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  createFormGroup(item?: ExtraUitgavenKledingEnBeddengoed): FormGroup {
    return new FormGroup({
      duurZiekteMinimaalJaar: new FormControl<boolean | null>(item?.duurZiekteMinimaalJaar ?? null),
      extraUitgavenKledingBeddengoedHogerDanMax: new FormControl<boolean | null>(item?.extraUitgavenKledingBeddengoedHogerDanMax ?? null),
      periode: new FormGroup({
        begindatum: new FormControl<Date | null>(
          item?.periode?.begindatum ??
          (this.incomeTaxEntity?.year != null ? new Date(this.incomeTaxEntity.year, 0, 1, 12, 0, 0) : null),
        ),
        einddatum: new FormControl<Date | null>(
          item?.periode?.einddatum ??
          (this.incomeTaxEntity?.year != null ? new Date(this.incomeTaxEntity.year, 11, 31, 12, 0, 0) : null),
        ),
      }),
    });
  }

  private updateForm(data: ExtraUitgavenKledingEnBeddengoed[]): void {
    const lijst = data || [];
    const formArray = this.extraUitgavenKledingEnBeddengoed;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    const extraUKenB = this.formGroup
      .getRawValue()
      .extraUitgavenKledingEnBeddengoed.filter((extra) => extra.periode)
      .map((extra) => ({
          duurZiekteMinimaalJaar: extra.duurZiekteMinimaalJaar,
          extraUitgavenKledingBeddengoedHogerDanMax: extra.extraUitgavenKledingBeddengoedHogerDanMax,
          periode: extra.periode,
        }) as ExtraUitgavenKledingEnBeddengoed,
      );

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          persoonsgebondenAftrek: {
            ...currentEntity.aangifte.persoonsgebondenAftrek,
            pgaSpecifiekeZorgkosten: {
              ...currentEntity.aangifte.persoonsgebondenAftrek?.pgaSpecifiekeZorgkosten,
              extraUitgavenKledingEnBeddengoed: extraUKenB,
            },
          },
        },
      } as IncomeTaxEntity;
    });
  }
}
